import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate, useParams } from 'react-router-dom';

import api from '../../utils/webcall';
import { getErrorMessage } from '../../utils/utils';
import AccessController from '../../components/access_controller';
import { getVizTokens } from '../analytics/vizTokens';
import { StatTile } from '../analytics/components';
import StaffFormDialog from './StaffFormDialog';
import {
    ADJUSTMENT_KINDS, ROLE_LABELS, dayCount, money, shortDate, todayIso,
} from './shared';

/** Record leave, or an advance / bonus / deduction. */
function EntryDialog(props) {
    const { open, mode, onClose, onSaved, staffId, advance, showErrorMessage } = props;
    const isMobile = useMediaQuery('(max-width:600px)');
    const isLeave = mode === 'leave';
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!open) return;
        setErrors({});
        setForm(isLeave
            ? { leave_type: 'unpaid', start_date: todayIso(), end_date: todayIso(), reason: '' }
            : { kind: 'advance', amount: '', date: todayIso(), note: '' });
    }, [open, isLeave]);

    const set = (field) => (event) => {
        setForm((prev) => ({ ...prev, [field]: event.target.value }));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const submit = () => {
        const found = {};
        if (isLeave) {
            if (!form.start_date) found.start_date = 'Required';
            if (!form.end_date) found.end_date = 'Required';
            if (form.start_date && form.end_date && form.end_date < form.start_date) {
                found.end_date = 'Cannot end before it starts';
            }
        } else {
            const amount = Number(form.amount);
            if (!form.amount || Number.isNaN(amount) || amount <= 0) {
                found.amount = 'Enter an amount greater than zero';
            }
            if (!form.date) found.date = 'Required';
        }
        setErrors(found);
        if (Object.keys(found).length) return;

        setSaving(true);
        const url = isLeave
            ? `/staff/${staffId}/leave/` : `/staff/${staffId}/adjustments/`;
        api.post(url, form)
            .then(() => onSaved())
            .catch((error) => showErrorMessage(getErrorMessage(error)))
            .finally(() => setSaving(false));
    };

    const selectedKind = ADJUSTMENT_KINDS.find((k) => k.value === form.kind);
    const overEntitlement = Boolean(
        advance && form.kind === 'advance' && form.amount
        && Number(form.amount) > Number(advance.available));

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose}
            fullWidth maxWidth="xs" fullScreen={isMobile}>
            <DialogTitle>{isLeave ? 'Record leave' : 'Record payment or deduction'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    {isLeave ? (
                        <>
                            <Grid size={{ xs: 12 }}>
                                <FormControl fullWidth>
                                    <InputLabel id="leave-type">Type</InputLabel>
                                    <Select labelId="leave-type" label="Type"
                                        value={form.leave_type || 'unpaid'}
                                        onChange={set('leave_type')}>
                                        <MenuItem value="paid">Paid leave (salary unaffected)</MenuItem>
                                        <MenuItem value="unpaid">Unpaid leave (deducted pro-rata)</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField label="From" type="date" fullWidth
                                    value={form.start_date || ''} onChange={set('start_date')}
                                    InputLabelProps={{ shrink: true }}
                                    error={Boolean(errors.start_date)} helperText={errors.start_date} />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField label="To" type="date" fullWidth
                                    value={form.end_date || ''} onChange={set('end_date')}
                                    InputLabelProps={{ shrink: true }}
                                    error={Boolean(errors.end_date)} helperText={errors.end_date} />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField label="Reason (optional)" fullWidth
                                    value={form.reason || ''} onChange={set('reason')} />
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid size={{ xs: 12 }}>
                                <FormControl fullWidth>
                                    <InputLabel id="kind">Type</InputLabel>
                                    <Select labelId="kind" label="Type"
                                        value={form.kind || 'advance'} onChange={set('kind')}>
                                        {ADJUSTMENT_KINDS.map((kind) => (
                                            <MenuItem key={kind.value} value={kind.value}>
                                                {kind.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                {/* Text, not type="number": a number field
                                    cannot be selected by triple-click and
                                    changes value on a stray scroll wheel, both
                                    of which are hazards on a box that hands out
                                    money. */}
                                <TextField label="Amount" fullWidth
                                    inputMode="decimal" value={form.amount || ''}
                                    onFocus={(e) => e.target.select()}
                                    onChange={(event) => {
                                        const value = event.target.value.replace(/[^\d.]/g, '');
                                        setForm((prev) => ({ ...prev, amount: value }));
                                        setErrors((prev) => ({ ...prev, amount: undefined }));
                                    }}
                                    error={Boolean(errors.amount)} helperText={errors.amount} />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField label="Date" type="date" fullWidth
                                    value={form.date || ''} onChange={set('date')}
                                    InputLabelProps={{ shrink: true }}
                                    error={Boolean(errors.date)} helperText={errors.date} />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField label="Note (optional)" fullWidth
                                    value={form.note || ''} onChange={set('note')} />
                            </Grid>
                            {selectedKind && (
                                <Grid size={{ xs: 12 }}>
                                    <Alert severity="info">
                                        {selectedKind.reduces
                                            ? 'This reduces the next payslip.'
                                            : 'This is added to the next payslip.'}
                                    </Alert>
                                </Grid>
                            )}
                            {/* What the hold rule would have allowed. Shown, not
                                enforced: an owner handing over cash in person
                                has already made the decision, and the cap is a
                                rule about what staff may ask for. */}
                            {form.kind === 'advance' && advance && (
                                <Grid size={{ xs: 12 }}>
                                    <Alert severity={overEntitlement ? 'warning' : 'success'}>
                                        {overEntitlement
                                            ? `This is more than the ${money(advance.available)} `
                                              + `earned so far beyond the ${advance.hold_days} `
                                              + 'days held back. You can still give it - it '
                                              + 'is recovered from later payslips until it '
                                              + 'clears.'
                                            : `Within the ${money(advance.available)} earned `
                                              + `beyond the ${advance.hold_days} days held back.`}
                                    </Alert>
                                </Grid>
                            )}
                        </>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function StaffDetail(props) {
    const { showErrorMessage, showSuccessMessage } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const tokens = useMemo(() => getVizTokens(theme.palette.mode === 'dark'),
        [theme.palette.mode]);
    const navigate = useNavigate();
    const { staffId } = useParams();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null);   // 'leave' | 'adjustment'
    const [editOpen, setEditOpen] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        api.get(`/staff/${staffId}/`).then((res) => {
            setData(res.data);
        }).catch((error) => {
            showErrorMessage(getErrorMessage(error));
        }).finally(() => setLoading(false));
    }, [staffId, showErrorMessage]);

    useEffect(() => { load(); }, [load]);

    const removeAdjustment = (id) => {
        api.delete(`/staff/${staffId}/adjustments/${id}/`)
            .then(() => { showSuccessMessage('Removed'); load(); })
            .catch((error) => showErrorMessage(getErrorMessage(error)));
    };

    const removeLeave = (id) => {
        api.delete(`/staff/${staffId}/leave/${id}/`)
            .then(() => { showSuccessMessage('Removed'); load(); })
            .catch((error) => showErrorMessage(getErrorMessage(error)));
    };

    const member = data?.staff;
    const leave = data?.leave_standing || {};

    return (
        <AccessController require="owner">
            <EntryDialog
                open={Boolean(dialog)} mode={dialog} staffId={staffId}
                advance={data?.advance}
                onClose={() => setDialog(null)}
                onSaved={() => { setDialog(null); load(); showSuccessMessage('Saved'); }}
                showErrorMessage={showErrorMessage} />
            <StaffFormDialog
                open={editOpen} member={member}
                onClose={() => setEditOpen(false)}
                onSaved={() => { setEditOpen(false); load(); showSuccessMessage('Updated'); }}
                showErrorMessage={showErrorMessage} />

            <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <IconButton aria-label="Back to staff" onClick={() => navigate('/staff')}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {member?.name || 'Staff'}
                    </Typography>
                    {member && !member.is_active && (
                        <Chip size="small" label="Former" variant="outlined" />
                    )}
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : !data ? (
                    <Alert severity="error">This staff member could not be loaded.</Alert>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                            {/* An advance handed over in person, never asked for
                                through the app. The entitlement is shown in the
                                dialog but does not block: what an owner gives
                                directly is their decision. */}
                            <Button variant="contained" fullWidth={isMobile}
                                onClick={() => setDialog('adjustment')}
                                sx={{ flex: isMobile ? '1 1 100%' : '0 0 auto' }}>
                                Give advance / bonus
                            </Button>
                            <Button variant="outlined" fullWidth={isMobile}
                                onClick={() => setDialog('leave')}
                                sx={{ flex: isMobile ? '1 1 100%' : '0 0 auto' }}>
                                Record leave
                            </Button>
                            <Button variant="outlined" fullWidth={isMobile}
                                onClick={() => setEditOpen(true)}
                                sx={{ flex: isMobile ? '1 1 100%' : '0 0 auto' }}>
                                Edit details
                            </Button>
                        </Box>

                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <StatTile tokens={tokens} label="Monthly salary"
                                    value={money(member.monthly_salary)} />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <StatTile tokens={tokens} label="Advances outstanding"
                                    value={money(data.outstanding_advances)}
                                    sub="recovered from the next payslip"
                                    tone={Number(data.outstanding_advances) > 0
                                        ? 'critical' : undefined} />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <StatTile tokens={tokens} label="Can draw now"
                                    value={money(data.advance?.available)}
                                    sub={`${dayCount(data.advance?.available_days)} clear of `
                                        + `the ${data.advance?.hold_days} held back`} />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <StatTile tokens={tokens} label="Joined"
                                    value={shortDate(member.joined_on)}
                                    sub={member.left_on
                                        ? `left ${shortDate(member.left_on)}`
                                        : ROLE_LABELS[member.role] || member.role} />
                            </Grid>
                        </Grid>

                        {/* Leave standing. Owner-only: this page is behind the
                            owner check, and none of it appears on anything a
                            staff member can open. */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <StatTile tokens={tokens} label="Leave accrued"
                                    value={dayCount(leave.balance)}
                                    sub={Number(leave.balance) < 0
                                        ? 'overdrawn' : 'available to take'}
                                    tone={Number(leave.balance) < 0 ? 'critical' : 'good'} />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <StatTile tokens={tokens} label="Leave forfeited"
                                    value={dayCount(leave.forfeited)}
                                    sub="over the yearly ceiling"
                                    tone={Number(leave.forfeited) > 0 ? 'critical' : undefined} />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <StatTile tokens={tokens} label="Entitlement"
                                    value={dayCount(leave.entitlement)}
                                    sub={`a year, ${dayCount(leave.monthly_accrual)} a month`} />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <StatTile tokens={tokens} label="Leave taken"
                                    value={dayCount(leave.taken)}
                                    sub={`since ${shortDate(leave.accrual_start)}`} />
                            </Grid>
                        </Grid>

                        {/* Payslip files are locked with this. It sits here
                            because the moment an owner needs it is the moment
                            they send a payslip on to someone. */}
                        <Alert severity={member.document_password ? 'info' : 'warning'}
                            sx={{ mb: 2 }}
                            action={(
                                <Button size="small" color="inherit"
                                    onClick={() => setEditOpen(true)}>
                                    {member.document_password ? 'Change' : 'Set one'}
                                </Button>
                            )}>
                            {member.document_password ? (
                                <>Payslip password: <strong>{member.document_password}</strong>{' '}
                                &mdash; send it with the file.</>
                            ) : (
                                <>No payslip password set. Payslip files are not
                                handed out until there is one.</>
                            )}
                        </Alert>

                        {Number(leave.forfeited) > 0 && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                {dayCount(leave.forfeited)} of leave have been lost by
                                sitting above the {dayCount(leave.entitlement)} ceiling.
                                Leave cannot build up past a year&rsquo;s worth.
                            </Alert>
                        )}
                        {Number(leave.balance) < 0 && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {member.name} has taken {dayCount(Math.abs(Number(leave.balance)))} more
                                paid leave than has been earned so far.
                            </Alert>
                        )}

                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Advances, bonuses and deductions
                        </Typography>
                        {data.adjustments.length === 0 ? (
                            <Typography variant="body2" sx={{ color: tokens.textMuted }}>
                                Nothing recorded yet.
                            </Typography>
                        ) : (
                            data.adjustments.map((row) => (
                                <Paper variant="outlined" key={row.id}
                                    sx={{ p: 1.5, mb: 1, display: 'flex', gap: 1,
                                        alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {row.kind_display}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                                            {row.date}{row.note ? ` · ${row.note}` : ''}
                                            {row.settled ? ' · settled' : ' · outstanding'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography sx={{
                                            fontWeight: 600, whiteSpace: 'nowrap',
                                            color: row.reduces_pay ? tokens.critical : tokens.good,
                                        }}>
                                            {row.reduces_pay ? '-' : '+'}{money(row.amount)}
                                        </Typography>
                                        {/* A settled adjustment belongs to a paid payslip
                                            and must not be removable. */}
                                        {!row.settled && (
                                            <IconButton size="small" aria-label="Remove"
                                                onClick={() => removeAdjustment(row.id)}>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Paper>
                            ))
                        )}

                        <Divider sx={{ my: 3 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Leave</Typography>
                        {data.leave.length === 0 ? (
                            <Typography variant="body2" sx={{ color: tokens.textMuted }}>
                                No leave recorded.
                            </Typography>
                        ) : (
                            data.leave.map((row) => (
                                <Paper variant="outlined" key={row.id}
                                    sx={{ p: 1.5, mb: 1, display: 'flex', gap: 1,
                                        alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {row.leave_type === 'paid' ? 'Paid leave' : 'Unpaid leave'}
                                            {' · '}{row.days} day{row.days === 1 ? '' : 's'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                                            {row.start_date} to {row.end_date}
                                            {row.reason ? ` · ${row.reason}` : ''}
                                        </Typography>
                                    </Box>
                                    <IconButton size="small" aria-label="Remove"
                                        onClick={() => removeLeave(row.id)}>
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Paper>
                            ))
                        )}

                        <Divider sx={{ my: 3 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Payslips</Typography>
                        {data.payslips.length === 0 ? (
                            <Typography variant="body2" sx={{ color: tokens.textMuted }}>
                                No payslips yet. Generate them from the Payroll page.
                            </Typography>
                        ) : (
                            data.payslips.map((slip) => (
                                <Paper variant="outlined" key={slip.id}
                                    onClick={() => navigate(`/staff/payslip/${slip.id}`)}
                                    sx={{ p: 1.5, mb: 1, cursor: 'pointer', display: 'flex',
                                        gap: 1, alignItems: 'center',
                                        justifyContent: 'space-between',
                                        '&:hover': { backgroundColor: 'action.hover' } }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {slip.period_start} to {slip.period_end}
                                        </Typography>
                                        <Chip size="small" sx={{ mt: 0.5 }}
                                            variant={slip.status === 'paid' ? 'filled' : 'outlined'}
                                            color={slip.status === 'paid' ? 'success' : 'default'}
                                            label={slip.status === 'paid'
                                                ? `Paid ${slip.paid_on || ''}` : 'Draft'} />
                                    </Box>
                                    <Typography sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                        {money(slip.net_payable)}
                                    </Typography>
                                </Paper>
                            ))
                        )}
                    </>
                )}
            </Box>
        </AccessController>
    );
}

export default StaffDetail;
